import { useState } from 'react';
import type { Movie } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';

interface DetailHeroProps {
  movie: Movie;
}

export default function DetailHero({ movie }: DetailHeroProps) {
  const { isFavorite, toggle } = useFavorites();
  const favorited = isFavorite(movie.id);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2000);
  };

  const handleFavorite = () => {
    toggle(movie.id);
    showToast(favorited ? '已取消收藏' : '已加入收藏');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  const metaItems = [
    { icon: 'ri-price-tag-3-line', label: '类型', value: movie.genres.join(' / ') },
    { icon: 'ri-earth-line', label: '地区', value: movie.region },
    { icon: 'ri-calendar-line', label: '年份', value: String(movie.year) },
    {
      icon: 'ri-time-line',
      label: movie.type === 'tv' ? '集数' : '片长',
      value: movie.type === 'tv' ? movie.updatedTo ?? `全${movie.episodes}集` : movie.duration ?? '—',
    },
    { icon: 'ri-clapperboard-line', label: '导演', value: movie.director },
    { icon: 'ri-user-star-line', label: '主演', value: movie.actors.join('、') },
  ];

  return (
    <div className="px-4 md:px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-8">
        <div className="max-w-[240px] lg:max-w-none mx-auto lg:mx-0 w-full">
          <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-300/60">
            <img
              src={movie.cover}
              alt={`${movie.title} 海报`}
              title={`${movie.title} 详情`}
              className="w-full h-full object-cover object-top"
            />
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-background-950/75 backdrop-blur text-accent-400 text-sm font-bold">
              <i className="ri-star-fill mr-1" />
              {movie.rating.toFixed(1)}
            </span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex-1 h-11 rounded-md text-sm font-medium whitespace-nowrap flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                favorited
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-200 text-foreground-900 hover:bg-background-300'
              }`}
            >
              <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} />
              {favorited ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="h-11 px-4 rounded-md bg-background-200 text-foreground-900 text-sm font-medium whitespace-nowrap hover:bg-background-300 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
              {movie.type === 'tv' ? '电视剧' : '电影'}
            </span>
            {movie.isHot && (
              <span className="px-3 py-1 rounded-full bg-accent-500/20 text-accent-400 text-xs font-medium">
                <i className="ri-fire-fill mr-1" />
                热播中
              </span>
            )}
            <span className="text-xs text-foreground-500">更新于 {movie.updateTime}</span>
          </div>

          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
            {movie.title}
          </h1>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {metaItems.map((item) => (
              <div key={item.label} className="flex items-start gap-2 text-sm">
                <i className={`${item.icon} text-foreground-500 mt-0.5`} />
                <span className="text-foreground-500 shrink-0">{item.label}：</span>
                <span className="text-foreground-800">{item.value}</span>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <h2 className="text-base font-semibold text-foreground-950 mb-2">剧情简介</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{movie.summary}</p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {movie.genres.map((g) => (
              <span
                key={g}
                className="px-3 py-1 rounded-full bg-secondary-100/70 text-secondary-900 text-xs"
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-background-950/90 backdrop-blur border border-background-300 text-sm text-foreground-900 animate-fade-in">
          <i className="ri-checkbox-circle-fill text-primary-400 mr-2" />
          {toast}
        </div>
      )}
    </div>
  );
}